import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip, FormHelperText } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { cssColorDark, cssSpacingMd } from '../constants/globalcss';
import { labelMaxWidth } from '../constants/globalcss';
import { ILabel } from '../constants/interfaces';

const useStyles = createUseStyles({
  chip: {
    margin: '2px',
  },
  container: {
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: cssSpacingMd,
    width: '100%',
  },
  inputContainer: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: cssColorDark,
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: cssColorDark,
      },
    },
    flex: 1,
  },
  labelContainer: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
    flexShrink: 0,
    width: labelMaxWidth,
  },
});

export interface MultiSelectOption {
  id: string | number;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  width?: string | number;
  maxSelections?: number;
  noBorder?: boolean;
  isLabelHidden?: boolean;
  field: ILabel;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  error = false,
  disabled = false,
  maxSelections,
  field,
}) => {
  const classes = useStyles();
  const [selectedValues, setSelectedValues] = useState<MultiSelectOption[]>([]);

  // Convert incoming value IDs to full option objects
  useEffect(() => {
    const selected = options.filter((option) => value.includes(option.label));
    setSelectedValues(selected);
  }, [value, options]);

  // Handle change and convert back to IDs
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: MultiSelectOption[]
  ) => {
    setSelectedValues(newValue);
    onChange(newValue.map((item) => item.label));
  };

  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <Autocomplete<MultiSelectOption, true, false, false>
          multiple
          id={`multi-select-${field.labelId}`}
          options={options}
          value={selectedValues}
          onChange={handleChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={option.label}
                {...getTagProps({ index })}
                className={classes.chip}
                disabled={disabled}
                key={option.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              error={error}
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
          disabled={disabled}
          limitTags={-1}
          disableCloseOnSelect
          filterSelectedOptions
          {...(maxSelections && {
            onChange: (_event, newValue) => {
              if (newValue.length <= maxSelections) {
                handleChange(_event, newValue);
              }
            },
          })}
        />

        {error && error && <FormHelperText error>{error}</FormHelperText>}

        {maxSelections && selectedValues.length > 0 && (
          <FormHelperText>
            {`Selected ${selectedValues.length} of ${maxSelections} maximum`}
          </FormHelperText>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
